import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Building,
  CheckCircle,
  Clock,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Send,
  Users,
} from "lucide-react";
import React, { useState } from "react";
import Footer from "../Footer";
import Header from "../Header";
import MiniFooter from "../MiniFooter";

const contactMethods = [
  {
    icon: MessageCircle,
    title: "Chat en direct",
    description: "Réponse immédiate",
    action: "Démarrer le chat",
  },
  {
    icon: Phone,
    title: "Téléphone",
    description: "Lun-Ven 9h-18h",
    action: "01 23 45 67 89",
  },
  {
    icon: Mail,
    title: "Email",
    description: "Réponse sous 24h",
    action: "contact@docuveritas.com",
  },
];

const contactDetails = [
  {
    icon: MapPin,
    title: "Adresse",
    details: ["15 Rue de la Paix", "75002 Paris, France"],
  },
  {
    icon: Phone,
    title: "Téléphone",
    details: ["+33 1 23 45 67 89", "Support : +33 1 23 45 67 90"],
  },
  {
    icon: Mail,
    title: "Email",
    details: ["contact@docuveritas.com", "support@docuveritas.com"],
  },
  {
    icon: Clock,
    title: "Horaires",
    details: ["Lun-Ven : 9h00 - 18h00", "Sam : 9h00 - 12h00"],
  },
];

const departments = [
  {
    icon: Users,
    title: "Équipe commerciale",
    email: "ventes@docuveritas.com",
    description: "Démonstrations, devis et partenariats",
  },
  {
    icon: Building,
    title: "Support technique",
    email: "support@docuveritas.com",
    description: "Assistance technique et formation",
  },
];

const faqs = [
  {
    question: "Comment puis-je obtenir une démonstration de Docuveritas ?",
    answer:
      "Vous pouvez demander une démonstration en remplissant notre formulaire de contact ou en nous appelant directement. Nos experts vous proposeront un créneau pour une présentation personnalisée de 30 minutes adaptée à vos besoins spécifiques.",
  },
  {
    question: "Quels sont vos délais de réponse ?",
    answer:
      "Nous nous engageons à répondre sous 24h pour les demandes commerciales, sous 4h pour le support technique et sous 2h pour les demandes urgentes. Les autres demandes reçoivent une réponse sous 48h maximum.",
  },
  {
    question: "Proposez-vous un support en français ?",
    answer:
      "Oui, notre équipe de support est entièrement francophone. Nous proposons également un support en anglais pour nos clients internationaux. Tous nos experts juridiques maîtrisent parfaitement le droit français.",
  },
  {
    question: "Comment fonctionne l'essai gratuit ?",
    answer:
      "L'essai gratuit de 14 jours vous donne accès à toutes les fonctionnalités de Docuveritas sans limitation. Aucune carte bancaire n'est requise pour commencer. À la fin de l'essai, vous pouvez choisir de souscrire à l'un de nos plans.",
  },
  {
    question: "Proposez-vous des tarifs pour les grandes organisations ?",
    answer:
      "Oui, nous proposons des tarifs préférentiels et des fonctionnalités avancées pour les cabinets d'avocats, entreprises et institutions de plus de 50 utilisateurs. Contactez notre équipe commerciale pour un devis personnalisé.",
  },
  {
    question: "Vos données sont-elles sécurisées ?",
    answer:
      "Absolument. Docuveritas est certifié ISO 27001 et conforme RGPD. Toutes les données sont chiffrées et hébergées en France. Nous appliquons les plus hauts standards de sécurité pour protéger vos informations confidentielles.",
  },
];

export default function Contact() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 2000));

    setIsLoading(false);
    setIsSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                Contactez-nous
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
                Notre équipe d'experts est là pour répondre à toutes vos
                questions sur{" "}
                <span className="text-slate-500 font-semibold">
                  Docuveritas
                </span>
                .
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Que vous souhaitiez une démonstration, des informations sur nos
                tarifs ou de l'aide technique, nous sommes à votre disposition
                pour vous accompagner.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {contactMethods.map((method, index) => (
                <div
                  key={index}
                  className="text-center p-8 bg-white rounded-2xl shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-6">
                    <method.icon className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {method.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{method.description}</p>
                  <p className="text-slate-500 font-semibold">
                    {method.action}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {isSubmitted && (
          <section className="py-20 bg-white">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
              <Card className="border-2 border-green-200 rounded-2xl">
                <CardContent className="p-12 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Message envoyé avec succès !
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Merci pour votre message. Notre équipe vous répondra dans
                    les plus brefs délais, généralement sous 24 heures.
                  </p>
                  <Button
                    onClick={() => setIsSubmitted(false)}
                    className="bg-slate-500 hover:bg-slate-600 text-white"
                  >
                    Envoyer un autre message
                  </Button>
                </CardContent>
              </Card>
            </div>
          </section>
        )}

        {!isSubmitted && (
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                {/* Form */}
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                    Envoyez-nous un message
                  </h2>
                  <p className="text-xl text-gray-600 mb-8">
                    Remplissez le formulaire ci-dessous et nous vous répondrons
                    rapidement.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label
                          htmlFor="firstName"
                          className="text-sm font-medium text-gray-700"
                        >
                          Prénom *
                        </Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          type="text"
                          required
                          className="mt-1 border-2 border-gray-200 focus:border-slate-500 rounded-xl"
                          placeholder="Votre prénom"
                        />
                      </div>
                      <div>
                        <Label
                          htmlFor="lastName"
                          className="text-sm font-medium text-gray-700"
                        >
                          Nom *
                        </Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          type="text"
                          required
                          className="mt-1 border-2 border-gray-200 focus:border-slate-500 rounded-xl"
                          placeholder="Votre nom"
                        />
                      </div>
                    </div>

                    <div>
                      <Label
                        htmlFor="email"
                        className="text-sm font-medium text-gray-700"
                      >
                        Email professionnel *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        className="mt-1 border-2 border-gray-200 focus:border-slate-500 rounded-xl"
                        placeholder="votre.email@entreprise.com"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label
                          htmlFor="company"
                          className="text-sm font-medium text-gray-700"
                        >
                          Entreprise/Cabinet
                        </Label>
                        <Input
                          id="company"
                          name="company"
                          type="text"
                          className="mt-1 border-2 border-gray-200 focus:border-slate-500 rounded-xl"
                          placeholder="Nom de votre organisation"
                        />
                      </div>
                      <div>
                        <Label
                          htmlFor="phone"
                          className="text-sm font-medium text-gray-700"
                        >
                          Téléphone
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          className="mt-1 border-2 border-gray-200 focus:border-slate-500 rounded-xl"
                          placeholder="01 23 45 67 89"
                        />
                      </div>
                    </div>

                    <div>
                      <Label
                        htmlFor="subject"
                        className="text-sm font-medium text-gray-700"
                      >
                        Sujet de votre demande *
                      </Label>
                      <Select name="subject" required>
                        <SelectTrigger className="mt-1 border-2 border-gray-200 focus:border-slate-500 rounded-xl">
                          <SelectValue placeholder="Sélectionnez un sujet" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="demo">
                            Demande de démonstration
                          </SelectItem>
                          <SelectItem value="pricing">
                            Informations tarifaires
                          </SelectItem>
                          <SelectItem value="technical">
                            Support technique
                          </SelectItem>
                          <SelectItem value="partnership">
                            Partenariat
                          </SelectItem>
                          <SelectItem value="other">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label
                        htmlFor="message"
                        className="text-sm font-medium text-gray-700"
                      >
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        rows={6}
                        className="mt-1 border-2 border-gray-200 focus:border-slate-500 rounded-xl"
                        placeholder="Décrivez votre demande en détail..."
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-slate-500 hover:bg-slate-600 text-white py-3 rounded-xl text-lg"
                    >
                      {isLoading ? (
                        "Envoi en cours..."
                      ) : (
                        <>
                          <Send className="mr-2 h-5 w-5" />
                          Envoyer le message
                        </>
                      )}
                    </Button>
                  </form>
                </div>

                {/* Contact Info */}
                <div className="lg:pl-8">
                  <Card className="border-2 border-gray-200 rounded-2xl mb-8">
                    <CardHeader>
                      <CardTitle className="text-xl text-gray-900">
                        Pourquoi nous contacter ?
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            Démonstration personnalisée
                          </h4>
                          <p className="text-sm text-gray-600">
                            Découvrez Docuveritas adapté à vos besoins
                            spécifiques
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            Conseil d'experts
                          </h4>
                          <p className="text-sm text-gray-600">
                            Nos juristes vous conseillent sur l'utilisation
                            optimale
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            Tarifs sur mesure
                          </h4>
                          <p className="text-sm text-gray-600">
                            Obtenez un devis adapté à votre organisation
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            Support technique
                          </h4>
                          <p className="text-sm text-gray-600">
                            Assistance pour l'intégration et la formation
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-slate-200 rounded-2xl">
                    <CardHeader>
                      <CardTitle className="text-xl text-gray-900">
                        Réponse garantie
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-4">
                        Nous nous engageons à répondre à toutes les demandes
                        dans les meilleurs délais :
                      </p>
                      <ul className="space-y-2 text-sm text-gray-600">
                        <li>
                          • <strong>Demandes urgentes :</strong> Sous 2 heures
                        </li>
                        <li>
                          • <strong>Demandes commerciales :</strong> Sous 24
                          heures
                        </li>
                        <li>
                          • <strong>Support technique :</strong> Sous 4 heures
                        </li>
                        <li>
                          • <strong>Autres demandes :</strong> Sous 48 heures
                        </li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Nos coordonnées
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Plusieurs moyens de nous joindre selon vos préférences et vos
                besoins
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              {contactDetails.map((contact, index) => (
                <Card
                  key={index}
                  className="border-2 hover:border-slate-300 transition-colors rounded-xl text-center"
                >
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-6">
                      <contact.icon className="h-8 w-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      {contact.title}
                    </h3>
                    <div className="space-y-2">
                      {contact.details.map((detail, detailIndex) => (
                        <p key={detailIndex} className="text-gray-600">
                          {detail}
                        </p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
              {departments.map((dept, index) => (
                <Card
                  key={index}
                  className="border-2 border-gray-200 rounded-2xl"
                >
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <dept.icon className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {dept.title}
                        </h3>
                        <p className="text-slate-500 font-semibold mb-2">
                          {dept.email}
                        </p>
                        <p className="text-gray-600">{dept.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Map placeholder */}
            <Card className="border-2 border-gray-200 rounded-2xl overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-gray-100 h-96 flex items-center justify-center">
                  <div className="text-center">
                    <MapPin className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">
                      Notre localisation
                    </h3>
                    <p className="text-gray-600">
                      15 Rue de la Paix, 75002 Paris
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Métro : Opéra (Lignes 3, 7, 8)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Questions fréquentes
              </h2>
              <p className="text-xl text-gray-600">
                Trouvez rapidement les réponses aux questions les plus courantes
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border-2 border-gray-200 rounded-xl px-6 hover:border-slate-300 transition-colors"
                >
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-slate-500">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed pt-2">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="text-center mt-12">
              <p className="text-gray-600 mb-4">
                Vous ne trouvez pas la réponse à votre question ?
              </p>
              <p className="text-slate-500 font-semibold">
                Contactez-nous directement au{" "}
                <a href="tel:+33123456789" className="hover:underline">
                  01 23 45 67 89
                </a>{" "}
                ou par{" "}
                <a
                  href="mailto:contact@docuveritas.com"
                  className="hover:underline"
                >
                  email
                </a>
              </p>
            </div>
          </div>
        </section>

        <MiniFooter />
      </main>
      <Footer />
    </div>
  );
}
